/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Heart, MessageSquare, EyeOff, Target, Edit3, Check, X, Sparkles, Crown, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { hideOnError } from "../lib/imgFallback";

const SUGGESTED = [10, 25, 50, 100];

/**
 * DonationCard — custom-amount tip / love-offering form.
 *
 * Shown on the Services tab of a Practitioner (PROVIDER) or Admin (ADMIN)
 * profile. The owner sees a goal-editor (Patreon-style fundraising bar).
 * Visitors see the progress bar (if a goal is set), the donation form, and
 * the recent-offerings social wall.
 *
 * Min $10, no max. 0% platform fee — funds go 100% to the recipient.
 */
export default function DonationCard({ recipientId, recipientName, viewerSignedIn, isOwner }) {
  const [selected, setSelected] = useState(25);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState({ donations: [], lifetimeTotal: 0, lifetimeCount: 0 });
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [goalProgress, setGoalProgress] = useState(null);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [topSupporters, setTopSupporters] = useState([]);

  const amount = custom ? parseFloat(custom) : selected;
  const valid = amount && amount >= 10;

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/donations/${recipientId}?limit=10`);
      setHistory(data);
    } catch {
      /* noop — non-critical */
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadGoal = async () => {
    try {
      const { data } = await api.get(`/donations/${recipientId}/goal`);
      setGoalProgress(data);
    } catch {
      setGoalProgress(null);
    }
  };

  useEffect(() => {
    if (!recipientId) return;
    let alive = true;
    (async () => {
      try {
        const [h, g, t] = await Promise.all([
          api.get(`/donations/${recipientId}?limit=10`),
          api.get(`/donations/${recipientId}/goal`),
          api.get(`/donations/${recipientId}/top-supporters?limit=5`),
        ]);
        if (!alive) return;
        setHistory(h.data);
        setGoalProgress(g.data);
        setTopSupporters(t.data?.supporters || []);
      } catch {
        /* noop */
      } finally {
        if (alive) setLoadingHistory(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId]);

  // Surface a toast when the user lands back from a successful Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("donate");
    const sessionId = params.get("session_id");
    if (status === "success" && sessionId) {
      (async () => {
        try {
          await api.get(`/checkout/status/${sessionId}`);
          toast.success(`Your donation has been delivered to ${recipientName || "the practitioner"}.`, {
            duration: 6000,
          });
          loadHistory();
        } catch {
          /* noop */
        }
        // Clean the URL so reload doesn't re-toast
        const cleaned = window.location.pathname;
        window.history.replaceState({}, "", cleaned);
      })();
    } else if (status === "cancel") {
      toast.info("Donation canceled. No charge was made.");
      const cleaned = window.location.pathname;
      window.history.replaceState({}, "", cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDonate = async () => {
    if (!viewerSignedIn) {
      toast.error("Please sign in to make a donation.");
      return;
    }
    if (!valid) {
      toast.error("Minimum donation is $10.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/init", {
        kind: "donation",
        recipientUserId: recipientId,
        donationAmountUsd: amount,
        donationMessage: message.trim() || undefined,
        donorAnonymous: anonymous,
        origin: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="donation-card-wrap">
      {/* Donation form — owners see it in preview mode (disabled submit) so
          they know exactly what visitors will see on their profile. */}
      <div className="parchment p-6 space-y-4" data-testid="donation-card">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="label-mystic text-secondary flex items-center gap-1.5">
              <Heart size={12} strokeWidth={1.75} /> Cauldron Custom Payment
            </div>
            <h3 className="font-serif text-2xl mt-1">
              Send {recipientName || "this practitioner"} a custom payment
            </h3>
            <p className="text-foreground/65 text-sm mt-1">
              For spiritual work. Cauldron takes a 1% platform fee — the rest goes directly to {recipientName?.split(" ")[0] || "them"}.
            </p>
          </div>
          {history.lifetimeCount > 0 && (
            <div className="text-right" data-testid="donation-lifetime-total">
              <div className="label-mystic">Lifetime received</div>
              <div className="font-serif text-2xl text-secondary">${history.lifetimeTotal.toFixed(0)}</div>
              <div className="text-[10px] text-foreground/55 uppercase tracking-[0.18em]">
                from {history.lifetimeCount} {history.lifetimeCount === 1 ? "seeker" : "seekers"}
              </div>
            </div>
          )}
        </div>

        {/* Suggested amount chips */}
        <div className="flex flex-wrap gap-2" data-testid="donation-suggested-amounts">
          {SUGGESTED.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => { setSelected(amt); setCustom(""); }}
              className={`px-5 py-2.5 rounded-sm text-sm font-serif transition-colors border ${
                !custom && selected === amt
                  ? "bg-secondary/15 text-secondary border-secondary"
                  : "bg-background/30 text-foreground/75 border-secondary/25 hover:border-secondary/60"
              }`}
              data-testid={`donation-amount-${amt}`}
            >
              ${amt}
            </button>
          ))}
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-sm">$</span>
            <input
              type="number"
              min="10"
              step="1"
              inputMode="decimal"
              placeholder="Custom"
              value={custom}
              onChange={(e) => setCustom(e.target.value.replace(/[^\d.]/g, ""))}
              className="w-32 pl-7 pr-3 py-2.5 bg-background/30 border border-secondary/25 rounded-sm text-sm focus:outline-none focus:border-secondary"
              data-testid="donation-custom-amount-input"
            />
          </div>
        </div>
        {custom && parseFloat(custom) > 0 && parseFloat(custom) < 10 && (
          <div className="text-xs text-destructive" data-testid="donation-min-warning">
            Minimum donation is $10.
          </div>
        )}

        {/* Optional message */}
        <div>
          <label className="label-mystic flex items-center gap-1.5 mb-1.5">
            <MessageSquare size={12} strokeWidth={1.75} /> Message (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 280))}
            placeholder={`Say a kind word to ${recipientName?.split(" ")[0] || "them"}…`}
            rows={2}
            className="w-full bg-background/30 border border-secondary/25 rounded-sm px-3 py-2 text-sm font-serif focus:outline-none focus:border-secondary resize-none"
            data-testid="donation-message-input"
          />
          <div className="text-[10px] text-foreground/45 text-right">{message.length}/280</div>
        </div>

        {/* Anonymous toggle */}
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="donation-anonymous-toggle">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
            className="w-4 h-4 accent-secondary"
            data-testid="donation-anonymous-checkbox"
          />
          <span className="text-xs text-foreground/75 flex items-center gap-1.5">
            <EyeOff size={11} strokeWidth={1.75} /> Donate anonymously
          </span>
        </label>

        {/* Submit */}
        <button
          type="button"
          disabled={!valid || submitting || isOwner}
          onClick={onDonate}
          className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
          data-testid="donation-submit-btn"
        >
          {isOwner
            ? "Preview — visitors will see this button"
            : submitting
              ? "Opening Stripe…"
              : valid
                ? `Donate $${amount.toFixed(amount % 1 === 0 ? 0 : 2)} via Stripe`
                : "Enter at least $10"}
        </button>
        <p className="text-[10px] text-foreground/50 text-center">
          {isOwner
            ? "Preview: this is what visitors see on your profile."
            : "Secure payment via Stripe. You'll be redirected to Stripe Checkout."}
        </p>
      </div>

      {/* Top supporters leaderboard — social proof */}
      {!loadingHistory && topSupporters.length > 0 && (
        <div className="parchment p-6 space-y-3" data-testid="donation-top-supporters">
          <div className="label-mystic text-secondary flex items-center gap-1.5">
            <Trophy size={12} strokeWidth={1.75} /> Top Supporters
          </div>
          <div className="space-y-2">
            {topSupporters.map((s, i) => (
              <div
                key={s.donorId || `anon-${i}`}
                className="flex items-center gap-3 py-1.5"
                data-testid={`donation-top-supporter-${i}`}
              >
                <div className={`w-7 h-7 shrink-0 flex items-center justify-center font-serif text-sm ${
                  i === 0 ? "text-secondary" : "text-foreground/50"
                }`}>
                  {i === 0 ? <Crown size={16} strokeWidth={1.75} /> : `#${i + 1}`}
                </div>
                <div className="w-9 h-9 shrink-0 rounded-full bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-sm text-secondary overflow-hidden">
                  {s.donorAvatarUrl ? (
                    <img src={s.donorAvatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
                  ) : s.anonymous ? (
                    <EyeOff size={14} strokeWidth={1.75} />
                  ) : (
                    (s.donorName?.[0] || "?").toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">
                  <div className="font-serif text-sm truncate">
                    {s.donorName}
                    {s.count > 1 && (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/45 ml-2">
                        × {s.count}
                      </span>
                    )}
                  </div>
                  <div className="font-serif text-base text-secondary">
                    ${s.total.toFixed(s.total % 1 === 0 ? 0 : 2)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent donations wall — social proof */}
      {!loadingHistory && history.donations.length > 0 && (
        <div className="parchment p-6 space-y-3" data-testid="donation-recent-list">
          <div className="label-mystic text-secondary flex items-center gap-1.5">
            <Heart size={12} strokeWidth={1.75} /> Recent Offerings
          </div>
          <div className="space-y-2.5">
            {history.donations.map((d) => (
              <div
                key={d.id}
                className="flex items-start gap-3 py-2 border-b border-secondary/10 last:border-0"
                data-testid={`donation-row-${d.id}`}
              >
                <div className="w-10 h-10 shrink-0 rounded-full bg-secondary/15 border border-secondary/40 flex items-center justify-center font-serif text-base text-secondary overflow-hidden">
                  {d.donorAvatarUrl ? (
                    <img src={d.donorAvatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
                  ) : d.anonymous ? (
                    <EyeOff size={14} strokeWidth={1.75} />
                  ) : (
                    (d.donorName?.[0] || "?").toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <div className="font-serif text-sm truncate">{d.donorName}</div>
                    <div className="font-serif text-base text-secondary">${d.amount.toFixed(d.amount % 1 === 0 ? 0 : 2)}</div>
                  </div>
                  {d.message && (
                    <div className="text-xs text-foreground/75 italic mt-0.5 break-words">"{d.message}"</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DonationGoalEditor — Patreon-style fundraising goal setup for the practitioner/admin
 * viewing their own profile. Lets them set/update/clear an amount, title, and period.
 */
function DonationGoalEditor({ existing, onSaved, onClose, showCloseBtn }) {
  const [amount, setAmount] = useState(existing?.amountUsd ? String(existing.amountUsd) : "");
  const [title, setTitle] = useState(existing?.title || "");
  const [period, setPeriod] = useState(existing?.period || "lifetime");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const a = parseFloat(amount);
    if (!a || a < 1) {
      toast.error("Enter a goal amount of $1 or more.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/providers/me/donation-goal", {
        goalUsd: a,
        goalTitle: title.trim() || null,
        period,
      });
      toast.success("Your donation goal is live.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save goal.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Remove your fundraising goal from your profile?")) return;
    setSaving(true);
    try {
      await api.put("/providers/me/donation-goal", { goalUsd: 0 });
      toast.success("Goal removed.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove goal.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="parchment p-6 space-y-4 border-secondary/30" data-testid="donation-goal-editor">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mystic text-secondary flex items-center gap-1.5">
            <Target size={12} strokeWidth={1.75} /> {existing ? "Update your goal" : "Set a fundraising goal"}
          </div>
          <p className="text-xs text-foreground/65 mt-1.5">
            A visible progress bar on your profile that motivates seekers to chip in toward something meaningful.
          </p>
        </div>
        {showCloseBtn && (
          <button
            type="button"
            onClick={onClose}
            className="text-foreground/50 hover:text-foreground"
            data-testid="donation-goal-editor-close"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        )}
      </div>

      <div>
        <label className="label-mystic mb-1.5 block">Goal amount (USD)</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-sm">$</span>
          <input
            type="number"
            min="1"
            step="1"
            inputMode="decimal"
            placeholder="500"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            className="w-full pl-7 pr-3 py-2.5 bg-background/30 border border-secondary/25 rounded-sm text-sm focus:outline-none focus:border-secondary"
            data-testid="donation-goal-amount-input"
          />
        </div>
      </div>

      <div>
        <label className="label-mystic mb-1.5 block">Why you're raising (optional)</label>
        <input
          type="text"
          maxLength={120}
          placeholder="Help me reach $500/mo to fund my temple"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2.5 bg-background/30 border border-secondary/25 rounded-sm text-sm font-serif focus:outline-none focus:border-secondary"
          data-testid="donation-goal-title-input"
        />
        <div className="text-[10px] text-foreground/45 text-right">{title.length}/120</div>
      </div>

      <div>
        <label className="label-mystic mb-1.5 block">Tracking period</label>
        <div className="flex gap-2">
          {[
            { id: "lifetime", label: "Lifetime" },
            { id: "monthly", label: "This month" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setPeriod(opt.id)}
              className={`px-4 py-2 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
                period === opt.id
                  ? "bg-secondary/15 text-secondary border-secondary"
                  : "bg-background/30 text-foreground/65 border-secondary/25 hover:border-secondary/60"
              }`}
              data-testid={`donation-goal-period-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !amount}
          className="bg-primary text-primary-foreground px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="donation-goal-save-btn"
        >
          <Check size={12} strokeWidth={2} /> {existing ? "Update goal" : "Publish goal"}
        </button>
        {existing && (
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            className="text-xs uppercase tracking-wider text-destructive hover:underline"
            data-testid="donation-goal-clear-btn"
          >
            Remove goal
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * MilestoneBanner — celebratory banner that appears under the goal title
 * when a 25/50/75/100% milestone has been recently crossed.
 *
 * Banner stays visible for 7 days after the milestone was reached, then
 * fades out (still tracked but not shown). When 100% is hit it stays
 * indefinitely — the goal is complete.
 */
function MilestoneBanner({ goal, percent }) {
  if (!goal) return null;
  const last = Number(goal.lastMilestonePct || 0);
  if (!last) return null;
  // Pick the highest tier the practitioner has already crossed (uses the
  // recorded high-water mark so the banner stays accurate even if a refund
  // dipped current progress below the threshold).
  const tiers = [100, 75, 50, 25];
  const effective = tiers.find((t) => t <= last) || last;

  const copy = {
    25: { title: "First quarter reached", body: "25% of the way there — momentum is building." },
    50: { title: "Halfway there", body: "50% reached. The temple is rising." },
    75: { title: "Home stretch", body: "75% reached — the goal is in sight." },
    100: { title: "Goal reached!", body: "100% funded. Thank you to every seeker who chipped in." },
  }[effective] || { title: `${effective}% reached`, body: "" };

  const isComplete = effective === 100;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-sm border ${
        isComplete
          ? "bg-secondary/15 border-secondary text-secondary"
          : "bg-secondary/8 border-secondary/40"
      }`}
      data-testid="donation-milestone-banner"
      data-milestone-pct={effective}
    >
      <Sparkles
        size={16}
        strokeWidth={1.75}
        className={isComplete ? "text-secondary mt-0.5 shrink-0" : "text-secondary/80 mt-0.5 shrink-0"}
      />
      <div className="flex-1 min-w-0">
        <div className="font-serif text-sm">{copy.title}</div>
        {copy.body && <div className="text-xs text-foreground/70 mt-0.5">{copy.body}</div>}
      </div>
    </div>
  );
}
